import React, { useState } from 'react'; // Removed local viewMode state
import { 
    Brain, Plus, Trash2, User, LogOut, Filter, X,
    FileText, Network, Eye, Loader2, CheckSquare,
    ChevronRight, ChevronDown, GitBranch, Layers, Info
} from 'lucide-react';

// --- Recursive Taxonomy Node Component (Unchanged) ---
const TaxonomyTreeNode = ({ node, selectedIds, handleToggleSelection, handleShowNodeProfile, taxonomyType, level = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedIds.has(node.id);

    const handleExpandClick = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="taxonomy-node-container">
            <div 
                className={`taxonomy-node-row ${isSelected ? 'selected-bg' : ''}`} 
                style={{ paddingLeft: `${level * 16 + 8}px` }} 
            >
                <button 
                    className="tree-toggle-btn" 
                    onClick={handleExpandClick}
                    style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>

                <input 
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSelection(node.id)}
                    className="tree-checkbox"
                />

                <button 
                    className="icon-btn"
                    style={{ marginRight: '8px', padding: '2px' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        // Pass node AND taxonomyType to the handler
                        handleShowNodeProfile(node, taxonomyType);
                    }}
                    title="View Node Profile"
                >
                    <Info size={14} style={{ color: 'var(--text-tertiary)' }} className="hover:text-blue-600" />
                </button>

                <span className="node-text" title={node.name}>{node.name}</span>
            </div>

            {isExpanded && hasChildren && (
                <div className="taxonomy-children">
                    {node.children.map(child => (
                        <TaxonomyTreeNode 
                            key={child.id} 
                            node={child} 
                            selectedIds={selectedIds} 
                            handleToggleSelection={handleToggleSelection}
                            handleShowNodeProfile={handleShowNodeProfile}
                            taxonomyType={taxonomyType} // Propagate type to children
                            level={level + 1} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

function Sidebar({ 
    currentUser, 
    userId, 
    onLogout, 
    knowledgeBases, 
    currentKB, 
    papersInView, 
    handleSelectActiveKBForOperations,
    handleDeletePaper,
    setShowCreateKbModal,
    handleTriggerKBFileUpload,
    handleCreateTaxonomy,
    handleShowPaperProfile,
    selectedPaperIds,
    handleTogglePaperSelection,
    handleClearPaperSelection,
    isUploading,
    // --- Data Props ---
    problemTaxonomyData, 
    methodTaxonomyData, 
    selectedProblemIds,
    handleToggleProblemSelection,
    selectedMethodIds,
    handleToggleMethodSelection,
    handleShowNodeProfile,
    // --- View Mode Props (Lifted State) ---
    viewMode, 
    setViewMode,
    loadingTaxonomy // Optional: to show loading state
}) {
    const [filterText, setFilterText] = useState('');

    let displayedPapers = papersInView.filter(paper => 
        paper.title.toLowerCase().includes(filterText.toLowerCase())
    );

    displayedPapers.sort((a, b) => {
        const isASelected = selectedPaperIds.has(a.id);
        const isBSelected = selectedPaperIds.has(b.id);
        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;
        return 0; 
    });

    const tabDefinitions = [
      { id: 'papers', label: 'Papers', icon: FileText },
      { id: 'problems', label: 'Problems', icon: GitBranch },
      { id: 'methods', label: 'Methods', icon: Layers },
    ];

    return (
        <aside className="static-sidebar">
            <div className="logo-group">
                <div className="logo-icon"><Brain size={28} /></div>
                <div>
                    <div className="logo-title">ScholarAssist</div>
                    <div className="logo-sub">Research Assistant</div>
                </div>
            </div>

            <div className="card sidebar-card kb-card">
                <div className="card-header">
                    <span>Current Knowledge Base</span>
                    <button className="header-add-btn" onClick={() => setShowCreateKbModal(true)}><Plus size={25} /></button>
                </div>
                
                <div className="select-wrapper">
                    <select 
                        className="kb-select" 
                        value={currentKB ? currentKB.id : ""}
                        onChange={(e) => handleSelectActiveKBForOperations(knowledgeBases.find(kb => kb.id === e.target.value))}
                    >
                        {!currentKB && <option value="">Select a KB...</option>}
                        {knowledgeBases.map(kb => (
                            <option key={kb.id} value={kb.id}>{kb.name}</option>
                        ))}
                    </select>
                </div>

                {/* --- Tab Switcher (Uses Lifted State) --- */}
                <div className="sidebar-tabs">
                    {tabDefinitions.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                className={`tab-btn ${viewMode === tab.id ? 'active' : ''}`}
                                onClick={() => setViewMode(tab.id)} // Calls parent handler
                                title={tab.label}
                            >
                                <Icon size={16} /> 
                                <span>{tab.label}</span>
                            </button>
                        )
                    })}
                </div>

                {/* Filter Controls (Papers Only) */}
                {viewMode === 'papers' && (
                    <div className="list-controls">
                        <div className="filter-wrapper">
                            <Filter className="filter-icon" size={12} />
                            <input 
                                type="text" 
                                className="filter-input" 
                                placeholder="Filter papers..." 
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            {filterText && <button className="clear-sel-btn" onClick={() => setFilterText('')}><X size={12} /></button>}
                            <button 
                                className="clear-sel-btn" 
                                onClick={() => {
                                    if (selectedPaperIds.size > 0) {
                                        handleClearPaperSelection();
                                    } else {
                                        // Select all visible papers
                                        papersInView.forEach(paper => handleTogglePaperSelection(paper.id));
                                    }
                                }}
                                style={{ 
                                    color: selectedPaperIds.size > 0 ? 'var(--primary-color)' : 'var(--text-tertiary)', 
                                    borderColor: selectedPaperIds.size > 0 ? 'var(--primary-color)' : 'var(--border-color)' 
                                }}
                                title={selectedPaperIds.size > 0 ? "Clear selection" : "Select all"}
                            >
                                <CheckSquare size={12} />
                            </button>
                        </div>
                    </div>
                )}
                
                {/* --- Content Area --- */}
                <div className="paper-list-container taxonomy-tree-container" style={{ overflowY: 'auto', flex: 1 }}>
                    
                    {/* VIEW 1: Paper List */}
                    {viewMode === 'papers' && (
                        displayedPapers.length === 0 ? (
                            <div className="empty-list-message">{filterText ? "No matches found." : "No papers in this KB."}</div>
                        ) : (
                            displayedPapers.map(paper => (
                                <div key={paper.id} className={`list-item paper-item ${selectedPaperIds.has(paper.id) ? 'selected-bg' : ''}`}>
                                    {/* <div className="list-item-right">
                                        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); handleShowPaperProfile(paper); }}><Eye size={12} /></button>
                                        <button className="icon-btn danger" onClick={(e) => { e.stopPropagation(); handleDeletePaper(currentKB.id, paper.id, paper.title); }}><Trash2 size={12} /></button>
                                    </div> */}
                                    <div className="list-item-left">
                                        <button className="icon-btn" onClick={(e) => { e.stopPropagation(); handleShowPaperProfile(paper); }}><Eye size={12} /></button>
                                        <button className="icon-btn danger" onClick={(e) => { e.stopPropagation(); handleDeletePaper(currentKB.id, paper.id, paper.title); }}><Trash2 size={12} /></button>
                                        <input type="checkbox" checked={selectedPaperIds.has(paper.id)} onChange={() => handleTogglePaperSelection(paper.id)} className="item-checkbox" />
                                        <FileText size={14} className="item-icon" />
                                        <span className="item-text" title={paper.title}>{paper.title}</span>
                                    </div>
                                    
                                </div>
                            ))
                        )
                    )}

                    {/* VIEW 2 & 3: Taxonomies */}
                    {(viewMode === 'problems' || viewMode === 'methods') && (
                        loadingTaxonomy ? (
                            <div className="flex justify-center p-4"><Loader2 className="animate-spin text-blue-500" /></div>
                        ) : (
                            (() => {
                                const data = viewMode === 'problems' ? problemTaxonomyData : methodTaxonomyData;
                                const selectedIds = viewMode === 'problems' ? selectedProblemIds : selectedMethodIds;
                                const toggleHandler = viewMode === 'problems' ? handleToggleProblemSelection : handleToggleMethodSelection;
                                // Determine single type string ('problem' or 'method')
                                const currentTaxonomyType = viewMode === 'problems' ? 'problem' : 'method';

                                if (!data) return <div className="empty-list-message">{viewMode === 'problems' ? 'Problem' : 'Method'} taxonomy not found. Please create one.</div>;

                                return Array.isArray(data) ? 
                                    data.map(node => (
                                        <TaxonomyTreeNode
                                            key={node.id}
                                            node={node}
                                            selectedIds={selectedIds}
                                            handleToggleSelection={toggleHandler}
                                            handleShowNodeProfile={handleShowNodeProfile}
                                            taxonomyType={currentTaxonomyType} // Pass type here
                                        />
                                    )) :
                                    <TaxonomyTreeNode
                                        node={data}
                                        selectedIds={selectedIds}
                                        handleToggleSelection={toggleHandler}
                                        handleShowNodeProfile={handleShowNodeProfile}
                                        taxonomyType={currentTaxonomyType} // Pass type here
                                    />
                            })()
                        )
                    )}
                </div>

                <div className="card-footer-actions">
                    <button className="action-btn-outline" onClick={() => handleCreateTaxonomy()} disabled={!currentKB || papersInView.length === 0}><Network size={14} /><span>Create Taxonomy</span></button>
                    <button className="action-btn-outline upload-btn" onClick={handleTriggerKBFileUpload} disabled={isUploading} style={{ opacity: isUploading ? 0.7 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}>{isUploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}<span>{isUploading ? "Uploading..." : "Upload Paper"}</span></button>
                </div>
            </div>

            <div className="sidebar-footer">
                <div className="user-profile">
                    <User className="user-avatar" size={24} />
                    <div className="user-info">
                        <div className="user-name">{currentUser?.name || "Researcher"}</div>
                    </div>
                </div>
                <button className="logout-btn" onClick={onLogout}><LogOut size={16} /><span>Log out</span></button>
            </div>
        </aside>
    );
}

export default Sidebar;
